import React, { useEffect, useCallback, useState } from 'react';
import RadioButtons from '../../../components/RadioButtons/RadioButtons';
import s from '../WatchtowerPage.module.scss';
import { useFlowmap } from '../FlowmapContext';
import { useFlowmapFilters } from '../hooks/useFlowmapFilters';
import FilterCheckboxList from '../components/FilterCheckboxList';
import { FILTER_TYPES } from '../utils/constants';
import Dropdown from '../../../components/Dropdown/Dropdown';
import Flag from '../../../components/Flag/Flag';
import Icon from '../../../components/Icon';

const Flowmap = ({ map, isFiltersShown = true, onFilterChange }) => {
    const {
        flowmapData,
        setFilteredFlowmapData,
        setActiveFilterCount,
        setResetFilters,
    } = useFlowmap();

    const {
        filters,
        filteredData,
        updateFilter,
        // highestValue,
        yearRange,
        availableOptions,
        activeFilters,
        resetFilters,
    } = useFlowmapFilters(flowmapData);

    const [localYear, setLocalYear] = useState(() => {
        return filters[FILTER_TYPES.YEAR] || null;
    });

    useEffect(() => {
        setActiveFilterCount(activeFilters);
    }, [activeFilters, setActiveFilterCount]);

    useEffect(() => {
        setResetFilters(() => resetFilters);
    }, [resetFilters, setResetFilters]);

    useEffect(() => {
        if (filteredData) {
            setFilteredFlowmapData(filteredData);
        }
    }, [filteredData, setFilteredFlowmapData]);

    // Update local year when filter year changes from external sources (URL, initialization)
    useEffect(() => {
        if (
            filters[FILTER_TYPES.YEAR] !== undefined &&
            filters[FILTER_TYPES.YEAR] !== localYear
        ) {
            setLocalYear(filters[FILTER_TYPES.YEAR]);
        }
    }, [filters[FILTER_TYPES.YEAR], localYear]);

    const handleYearRadioChange = useCallback(
        (selectedYear) => {
            onFilterChange?.();
            const year = selectedYear ? parseInt(selectedYear, 10) : null;
            setLocalYear(year);
            updateFilter(FILTER_TYPES.YEAR, year);
        },
        [updateFilter, onFilterChange]
    );

    const handleFromCountriesToggle = useCallback(
        (newValue) => {
            onFilterChange?.();
            updateFilter(FILTER_TYPES.FROM_COUNTRIES, newValue);
        },
        [updateFilter, onFilterChange]
    );

    const handleToCountriesToggle = useCallback(
        (newValue) => {
            onFilterChange?.();
            updateFilter(FILTER_TYPES.TO_COUNTRIES, newValue);
        },
        [updateFilter, onFilterChange]
    );

    const handleSourcesToggle = useCallback(
        (newValue) => {
            updateFilter(FILTER_TYPES.SOURCES, newValue);
        },
        [updateFilter]
    );

    const handleTypesChange = useCallback(
        (selectedValue) => {
            onFilterChange?.();
            updateFilter(
                FILTER_TYPES.TYPES,
                selectedValue ? [selectedValue] : []
            );
        },
        [updateFilter, onFilterChange]
    );

    const handleHsCodesToggle = useCallback(
        (newValue) => {
            onFilterChange?.();
            updateFilter(FILTER_TYPES.HS_CODES, newValue);
        },
        [updateFilter, onFilterChange]
    );

    const handleUnitsToggle = useCallback(
        (newValue) => {
            updateFilter(FILTER_TYPES.UNITS, newValue);
        },
        [updateFilter]
    );

    // Always run filtering logic above, only conditionally render UI
    if (!flowmapData?.filters) {
        return null;
    }

    // If filters aren't shown, don't render UI but filtering logic still ran above
    if (!isFiltersShown) {
        return null;
    }

    const disabledCategoryOptions = new Set(
        flowmapData.filters.types
            .map((item) => item.id)
            .filter((id) => !availableOptions.availableCategories.has(id))
    );

    const disabledYearOptions = new Set(
        yearRange.availableYears
            ?.filter((year) => !availableOptions.availableYears.has(year))
            .map((year) => year.toString()) || []
    );

    const allYearOptionsDisabled =
        yearRange.availableYears?.every(
            (year) => !availableOptions.availableYears.has(year)
        ) ?? false;

    const allFromCountriesDisabled = flowmapData.filters.from_countries.every(
        (item) => !availableOptions[FILTER_TYPES.FROM_COUNTRIES].has(item.id)
    );

    const allToCountriesDisabled = flowmapData.filters.to_countries.every(
        (item) => !availableOptions[FILTER_TYPES.TO_COUNTRIES].has(item.id)
    );

    const allProductsDisabled = flowmapData.filters.hs_codes.every(
        (item) => !availableOptions[FILTER_TYPES.HS_CODES].has(item.id)
    );

    return (
        <div className={s.FilterList}>
            <div className={s.FilterItem}>
                <div className={s.Box}>
                    <h3 className={s.FilterContentHeader}>Category</h3>
                    <div className={s.FilterContent}>
                        <Dropdown
                            title={
                                filters[FILTER_TYPES.TYPES]?.[0]
                                    ? flowmapData.filters.types.find(
                                          (item) =>
                                              item.id ===
                                              filters[FILTER_TYPES.TYPES][0]
                                      )?.label
                                    : 'Select category'
                            }
                            showShadow
                            modifiers={['RoundedBorders']}>
                            <div className={s.FilterContentWrapper}>
                                <RadioButtons
                                    showCheckmark
                                    options={flowmapData.filters.types.map(
                                        (item) => ({
                                            value: item.id,
                                            label: item
                                                ? item.label
                                                      .charAt(0)
                                                      .toUpperCase() +
                                                  item.label.slice(1)
                                                : '',
                                        })
                                    )}
                                    value={
                                        filters[FILTER_TYPES.TYPES]?.[0] || null
                                    }
                                    onChange={handleTypesChange}
                                    name="categoryFilter"
                                    modifiers={['InDropdown']}
                                    disabledOptions={disabledCategoryOptions}
                                />
                            </div>
                        </Dropdown>
                    </div>
                </div>

                <div className={s.Box}>
                    <h3 className={s.FilterContentHeader}>Year</h3>
                    <div className={s.FilterContent}>
                        <Dropdown
                            title={
                                localYear ? localYear.toString() : 'Select year'
                            }
                            showShadow
                            disabled={allYearOptionsDisabled}
                            modifiers={['RoundedBorders']}>
                            <div className={s.FilterContentWrapper}>
                                <RadioButtons
                                    showCheckmark
                                    options={
                                        yearRange.availableYears?.map(
                                            (year) => ({
                                                value: year.toString(),
                                                label: year.toString(),
                                            })
                                        ) || []
                                    }
                                    value={localYear?.toString() || null}
                                    onChange={handleYearRadioChange}
                                    name="yearFilter"
                                    modifiers={['InDropdown']}
                                    disabledOptions={disabledYearOptions}
                                />
                            </div>
                        </Dropdown>
                    </div>
                </div>

                <div className={s.Box}>
                    <h3 className={s.FilterContentHeader}>Origin</h3>
                    <div className={s.FilterContent}>
                        <Dropdown
                            title="Select countries"
                            disabled={allFromCountriesDisabled}
                            description={
                                filters[FILTER_TYPES.FROM_COUNTRIES]?.length > 0
                                    ? `(${filters[FILTER_TYPES.FROM_COUNTRIES].length})`
                                    : ''
                            }
                            showShadow
                            modifiers={['RoundedBorders']}>
                            <FilterCheckboxList
                                modifiers={['InDropdown']}
                                items={flowmapData.filters.from_countries}
                                selectedItems={
                                    filters[FILTER_TYPES.FROM_COUNTRIES]
                                }
                                onItemToggle={handleFromCountriesToggle}
                                showFlags={true}
                                availableItems={
                                    availableOptions[
                                        FILTER_TYPES.FROM_COUNTRIES
                                    ]
                                }
                            />
                        </Dropdown>
                        {filters[FILTER_TYPES.FROM_COUNTRIES]?.length > 0 && (
                            <div className={s.SelectedCountries}>
                                {filters[FILTER_TYPES.FROM_COUNTRIES].map(
                                    (countryId) => {
                                        const country =
                                            flowmapData.filters.from_countries.find(
                                                (c) => c.id === countryId
                                            );
                                        if (!country) return null;

                                        return (
                                            <div
                                                key={country.id}
                                                className={s.SelectedCountry}>
                                                <div
                                                    className={
                                                        s.SelectedCountryNameWrapper
                                                    }>
                                                    {country.id && (
                                                        <Flag
                                                            countryCode={
                                                                country.id
                                                            }
                                                            width="2.4rem"
                                                        />
                                                    )}
                                                    <span
                                                        className={
                                                            s.SelectedCountryText
                                                        }>
                                                        {country.label}
                                                    </span>
                                                </div>
                                                <button
                                                    type="button"
                                                    className={s.RemoveBtn}
                                                    onClick={() =>
                                                        handleFromCountriesToggle(
                                                            (prev) =>
                                                                prev.filter(
                                                                    (item) =>
                                                                        item !==
                                                                        country.id
                                                                )
                                                        )
                                                    }>
                                                    <Icon name="Close" />
                                                </button>
                                            </div>
                                        );
                                    }
                                )}
                            </div>
                        )}
                    </div>
                </div>

                <div className={s.Box}>
                    <h3 className={s.FilterContentHeader}>Destination</h3>
                    <div className={s.FilterContent}>
                        <Dropdown
                            title="Select countries"
                            disabled={allToCountriesDisabled}
                            description={
                                filters[FILTER_TYPES.TO_COUNTRIES]?.length > 0
                                    ? `(${filters[FILTER_TYPES.TO_COUNTRIES].length})`
                                    : ''
                            }
                            showShadow
                            modifiers={['RoundedBorders']}>
                            <FilterCheckboxList
                                modifiers={['InDropdown']}
                                items={flowmapData.filters.to_countries}
                                selectedItems={
                                    filters[FILTER_TYPES.TO_COUNTRIES]
                                }
                                onItemToggle={handleToCountriesToggle}
                                showFlags={true}
                                availableItems={
                                    availableOptions[FILTER_TYPES.TO_COUNTRIES]
                                }
                            />
                        </Dropdown>
                        {filters[FILTER_TYPES.TO_COUNTRIES]?.length > 0 && (
                            <div className={s.SelectedCountries}>
                                {filters[FILTER_TYPES.TO_COUNTRIES].map(
                                    (countryId) => {
                                        const country =
                                            flowmapData.filters.to_countries.find(
                                                (c) => c.id === countryId
                                            );
                                        if (!country) return null;

                                        return (
                                            <div
                                                key={country.id}
                                                className={s.SelectedCountry}>
                                                <div
                                                    className={
                                                        s.SelectedCountryNameWrapper
                                                    }>
                                                    <Flag
                                                        countryCode={country.id}
                                                        width="2.4rem"
                                                    />
                                                    <span
                                                        className={
                                                            s.SelectedCountryText
                                                        }>
                                                        {country.label}
                                                    </span>
                                                </div>
                                                <button
                                                    type="button"
                                                    className={s.RemoveBtn}
                                                    onClick={() =>
                                                        handleToCountriesToggle(
                                                            (prev) =>
                                                                prev.filter(
                                                                    (item) =>
                                                                        item !==
                                                                        country.id
                                                                )
                                                        )
                                                    }>
                                                    <Icon name="Close" />
                                                </button>
                                            </div>
                                        );
                                    }
                                )}
                            </div>
                        )}
                    </div>
                </div>

                <div className={s.Box}>
                    <h3 className={s.FilterContentHeader}>Product</h3>
                    <div className={s.FilterContent}>
                        <Dropdown
                            title="Select products"
                            disabled={allProductsDisabled}
                            description={
                                filters[FILTER_TYPES.HS_CODES]?.length > 0
                                    ? `(${filters[FILTER_TYPES.HS_CODES].length})`
                                    : ''
                            }
                            showShadow
                            modifiers={['RoundedBorders']}>
                            <FilterCheckboxList
                                modifiers={['InDropdown']}
                                items={flowmapData.filters.hs_codes}
                                selectedItems={filters[FILTER_TYPES.HS_CODES]}
                                onItemToggle={handleHsCodesToggle}
                                availableItems={
                                    availableOptions[FILTER_TYPES.HS_CODES]
                                }
                            />
                        </Dropdown>
                        {filters[FILTER_TYPES.HS_CODES]?.length > 0 && (
                            <div className={s.SelectedCountries}>
                                {filters[FILTER_TYPES.HS_CODES].map(
                                    (hsCode) => {
                                        const product =
                                            flowmapData.filters.hs_codes.find(
                                                (p) => p.id === hsCode
                                            );
                                        if (!product) return null;

                                        return (
                                            <div
                                                key={product.id}
                                                className={s.SelectedCountry}>
                                                <div
                                                    className={
                                                        s.SelectedCountryNameWrapper
                                                    }>
                                                    <span
                                                        className={
                                                            s.SelectedCountryText
                                                        }>
                                                        {product.label}
                                                    </span>
                                                </div>
                                                <button
                                                    type="button"
                                                    className={s.RemoveBtn}
                                                    onClick={() =>
                                                        handleHsCodesToggle(
                                                            (prev) =>
                                                                prev.filter(
                                                                    (item) =>
                                                                        item !==
                                                                        product.id
                                                                )
                                                        )
                                                    }>
                                                    <Icon name="Close" />
                                                </button>
                                            </div>
                                        );
                                    }
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Flowmap;
